import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { Language } from '../i18n/i18n';
import { routes } from '../routes/routes';
import { AccountStatus } from './account-status.enum';
import { AuthService } from './auth.service';

export interface InvitableUserDto {
  id: string;
  email: string;
  firstname: string;
  lastname: string;
  accountStatus: AccountStatus;
  pendingInvite: boolean;
}

export interface InviteUsersResult<T> {
  invited: T[];
  emailsErrored: string[]; // Contains e-mail addy's where invitation could not complete.
}

const emailLinkExistingUserParam = 'existingUser';

@Injectable({ providedIn: 'root' })
export class UserInvitationService {
  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private translateService: TranslateService,
  ) {}

  async inviteUsers<T extends InvitableUserDto>(endpoint: string, emails: string[]): Promise<InviteUsersResult<T>> {
    const requests = emails.map(email => this.inviteUser<T>(endpoint, email));
    const responses = await Promise.allSettled(requests);
    const result: InviteUsersResult<T> = { invited: [], emailsErrored: [] };
    responses.forEach((response, index) => {
      if (response.status === 'fulfilled') {
        result.invited.push(response.value);
      } else {
        result.emailsErrored.push(emails[index]);
      }
    });
    return result;
  }

  async resendSignInLinkToEmail(email: string): Promise<void> {
    await this.authService.sendSignInLinkToEmail(email, `${this.invitationLoginUrl()}?${emailLinkExistingUserParam}=1`);
  }

  async signInWithEmailLink(
    link: string,
    userData: {
      email: string;
      firstname: string;
      lastname: string;
    },
  ): Promise<void> {
    await this.authService.signInWithEmailLink(link, userData.email);
    await this.authService.updateUser({
      firstname: userData.firstname,
      lastname: userData.lastname,
      language: this.translateService.currentLang as Language,
    });
  }

  isSignInWithEmailLinkForExistingUser(link: string): boolean {
    return this.authService.isValidSignInWithEmailLink(link) && new URL(link).searchParams.has(emailLinkExistingUserParam);
  }

  private async inviteUser<T extends InvitableUserDto>(endpoint: string, email: string): Promise<T> {
    const dto = await firstValueFrom(this.http.post<T>(endpoint, { email }));

    await this.authService.sendSignInLinkToEmail(dto.email, this.invitationLoginUrl());

    return dto;
  }

  private invitationLoginUrl() {
    const url = routes.userInvitationLogin.url();
    return `${window.location.origin}${url}`;
  }
}
